import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  AnalyzerResult,
  ElasticsearchAnalyzerIndexTarget,
  SearchService,
} from '@libs/shared/api';
import { AnalyzeTokenRendererComponent } from '@libs/shared/ui/elasticsearch-term-analyzer/analyze-token-renderer.component';
import { ElasticsearchAnalyzeTokenInlineResultComponent } from '@libs/shared/ui/elasticsearch-term-analyzer/elasticsearch-analyze-token-inline-result.component';
import { catchError, Observable } from 'rxjs';

@Component({
  selector: 'app-elasticsearch-multi-analyzer-list',
  standalone: true,
  imports: [
    CommonModule,
    ElasticsearchAnalyzeTokenInlineResultComponent,
    AnalyzeTokenRendererComponent,
  ],
  template: `
    @if (indexKeyword) {
      <div>
        <table class="min-w-full bg-white dark:bg-gray-800">
          <thead>
            <tr>
              <th
                class="px-4 py-3 text-left text-sm font-semibold uppercase dark:text-white">
                Analyzer
              </th>
              <th
                class="px-4 py-3 text-left text-sm font-semibold uppercase dark:text-white">
                Index Result
              </th>
              <th
                class="px-4 py-3 text-left text-sm font-semibold uppercase dark:text-white">
                Search Result
              </th>
            </tr>
          </thead>
          @if (analyzeTokens$ | async; as analyzeTokens) {
            <tbody>
              @for (analyzerResult of analyzeTokens; track analyzerResult) {
                <tr>
                  <td class="px-4 py-3 text-left text-sm dark:text-white">
                    {{ transformAnalyzerName(analyzerResult.groupType) }}
                  </td>
                  <td class="px-4 py-3 text-left" style="vertical-align: top;">
                    <app-analyze-token-renderer
                      [tokens]="analyzerResult.indexTokens"
                      [lineBreak]="true"
                      [textSize]="textSize" />
                  </td>
                  <td class="px-4 py-3 text-left" style="vertical-align: top;">
                    <app-analyze-token-renderer
                      [tokens]="analyzerResult.searchTokens"
                      [lineBreak]="true"
                      [textSize]="textSize" />
                  </td>
                </tr>
              }
            </tbody>
          }
        </table>
        @if (error) {
          <div class="text-red-600 dark:text-red-400">
            <p>{{ error }}</p>
          </div>
        }
      </div>
    } @else {
      <div class="flex h-60 items-center justify-center">
        <p class="text-lg font-semibold text-gray-600 dark:text-gray-300">
          키워드가 없습니다. 분석할 텍스트를 입력해주세요.
        </p>
      </div>
    }
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ElasticsearchMultiAnalyzerListComponent implements OnChanges {
  @Input() indexKeyword: string;
  @Input() searchKeyword: string;
  @Input() indexTarget: ElasticsearchAnalyzerIndexTarget = 'Exercise';

  @Input() textSize: 'small' | 'medium' | 'large' = 'small'; // Default size

  analyzeTokens$: Observable<AnalyzerResult[]>;
  error: string;

  constructor(private _searchService: SearchService) {}

  onFetchAnalyzeTokens(indexKeyword: string, searchKeyword: string) {
    this.analyzeTokens$ = this._searchService
      .analyzeAllTerm(indexKeyword, searchKeyword, this.indexTarget) // 서비스 메서드 수정 필요
      .pipe(
        catchError(err => {
          this.error = err.message || 'An error occurred';
          return [];
        })
      );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.indexKeyword || changes.searchKeyword) {
      this.onFetchAnalyzeTokens(this.indexKeyword, this.searchKeyword);
    }
  }
  transformAnalyzerName(name: string): string {
    return name.replace('Analyzer', '');
  }
}
